import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import lang from '../../../../lib/language';
import { MarketCapCategory } from '../../../../lib/models/fundamental/types';

import iconSort from '../../../../assets/img/sort.png';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileDrawer from '../../../MobileDrawer/MobileDrawer';
import { MARKET_CAP_CATEGORY_OPTIONS } from '../../MarketCapRadar.constants';
import { getMarketCapCategoryLabel } from '../../marketCapRadar.helpers';

import styles from './MarketCapFilterDropDown.module.scss';

type Props = {
  selectedCategory: MarketCapCategory;
  handleCategoryChange: (category: MarketCapCategory) => void;
  isDisabled?: boolean;
  /** Renders the chip at the height of the NavButtons tab strip it sits next to. */
  isInTabsRow?: boolean;
};

const MarketCapFilterDropDown = (
  { selectedCategory, handleCategoryChange, isDisabled, isInTabsRow }: Props,
): JSX.Element => {
  const { isMobile } = useIsMobile();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleDropdown = () => {
    if (!isDisabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionClick = (category: MarketCapCategory) => {
    handleCategoryChange(category);
    setIsDropdownOpen(false);
    setIsFilterDrawerOpen(false);
  };

  const optionRows = (optionClassName: string) => MARKET_CAP_CATEGORY_OPTIONS.map(option => (
    <ClickableDiv
      key={option.category}
      className={classNames(optionClassName, { [styles.selected]: option.category === selectedCategory })}
      onClick={() => handleOptionClick(option.category)}
    >
      <span className={styles.optionLabel}>{option.label}</span>
      <span className={styles.optionRange}>{option.range}</span>
    </ClickableDiv>
  ));

  return (
    <ClickableDiv
      ref={dropdownRef}
      onClick={handleToggleDropdown}
      className={classNames(styles.wrapper, {
        [styles.open]: isDropdownOpen,
        [styles.inTabsRow]: isInTabsRow,
      })}
    >
      {!isMobile && (
        <>
          <div className={styles.iconWrapper}>
            <img src={iconSort} className={styles.icon} alt="sort" />
          </div>
          <div className={classNames(styles.desktopDropdownTrigger, { [styles.disabled]: isDisabled })}>
            {getMarketCapCategoryLabel(selectedCategory)}
          </div>
          {isDropdownOpen && (
            <div className={styles.desktopDropdownMenu}>
              {optionRows(styles.desktopDropdownOption)}
            </div>
          )}
        </>
      )}

      {isMobile && (
        <>
          <ClickableDiv
            disabled={isDisabled}
            className={styles.mobFilterButton}
            onClick={() => setIsFilterDrawerOpen(true)}
          >
            <img src={iconSort} alt="icon sort" className={styles.mobFilterButtonIcon} />
            <span className={styles.mobFilterButtonText}>{getMarketCapCategoryLabel(selectedCategory)}</span>
          </ClickableDiv>
          <MobileDrawer
            isOpen={isFilterDrawerOpen}
            title={lang.marketCapRadarMarketCap()}
            onClose={() => setIsFilterDrawerOpen(false)}
          >
            <div className={styles.rangeButtonWrapper}>
              {optionRows(styles.rangeButton)}
            </div>
          </MobileDrawer>
        </>
      )}
    </ClickableDiv>
  );
};

export default MarketCapFilterDropDown;
